import React from "react";
import { Button, Progress, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { RecommendationRun } from "../../api/RecommendationRunApi";
import { isRecommendationRunCancelled } from "../../helpers/RecommendationRunHelper";
import { formatRecommendationMessage } from "../../helpers/RecommendationMessageHelper";
import styles from "./RecommendationRunBanner.module.scss";

type RecommendationRunBannerProps = {
  run: RecommendationRun;
  variant?: "banner" | "popover" | "mobile";
  onCancel?: () => void;
  onViewHistory?: () => void;
  onGenerate?: () => void;
  cancelling?: boolean;
  generating?: boolean;
};

const RecommendationRunBanner = ({
  run,
  variant = "banner",
  onCancel,
  onViewHistory,
  onGenerate,
  cancelling = false,
  generating = false,
}: RecommendationRunBannerProps) => {
  const { t } = useTranslation();
  const cancelled = isRecommendationRunCancelled(run);
  const isSuccess = run.status === "SUCCESS" && !cancelled;
  const isFailed = run.status === "FAILED" || cancelled;
  const isFinished = isSuccess || isFailed;
  const statusLabel = isSuccess
    ? t("RecommendationRun-Finished")
    : isFailed
    ? t(cancelled ? "RecommendationRun-Cancelled" : "RecommendationRun-Failed")
    : t("RecommendationRun-InProgress");
  const containerClassName =
    variant === "popover"
      ? styles.popoverCard
      : variant === "mobile"
      ? styles.mobileCard
      : styles.banner;
  const showActions = variant === "popover" || variant === "mobile";

  return (
    <div className={containerClassName}>
      <div className={styles.header}>
        <div>
          <Typography.Text strong className={styles.title}>
            {t("RecommendationRun-Title")}
          </Typography.Text>
          <Typography.Text className={styles.subtitle}>
            {formatRecommendationMessage(
              t,
              run.current_step_label_key,
              run.current_step_label_params,
              run.current_step_label
            ) || t("RecommendationRun-Preparing")}
          </Typography.Text>
          {run.current_step_detail && (
            <Typography.Text type="secondary" className={styles.stepDetail}>
              {run.current_step_detail}
            </Typography.Text>
          )}
        </div>
        <Tag color={isSuccess ? "green" : isFailed ? "red" : "blue"}>
          {statusLabel}
        </Tag>
      </div>

      <Progress
        percent={Math.max(
          0,
          Math.min(100, Math.round(run.percent_completed || 0))
        )}
        status={isFailed ? "exception" : isSuccess ? "success" : "active"}
      />

      <div className={styles.metrics}>
        <span>
          {t("RecommendationRun-Segments")}: {run.segments_generated || 0}
        </span>
        <span>
          {t("RecommendationRun-CommercialOpportunities")}:{" "}
          {run.commercial_opportunities_generated || 0}
        </span>
        <span>
          {t("RecommendationRun-Rewards")}: {run.rewards_generated || 0}
        </span>
        <span>
          {t("RecommendationRun-Promotions")}: {run.promotions_generated || 0}
        </span>
        <span>
          {t("RecommendationRun-Actions")}: {run.actions_generated || 0}
        </span>
      </div>

      {run.error_message && (
        <Typography.Paragraph
          className={styles.errorText}
          ellipsis={{
            rows: 3,
            expandable: true,
            symbol: t("RecommendationRun-ShowMore"),
            tooltip: run.error_message,
          }}
        >
          {run.error_message}
        </Typography.Paragraph>
      )}

      {showActions &&
        (onViewHistory || onGenerate || (run.active && onCancel)) && (
          <div className={styles.actions}>
            {isFinished && onGenerate && (
              <Button
                type="primary"
                block
                onClick={onGenerate}
                loading={generating}
              >
                {t("NavBar-GenerateRecommendations")}
              </Button>
            )}
            {onViewHistory && (
              <Button block onClick={onViewHistory}>
                {t("RecommendationRun-ViewHistory")}
              </Button>
            )}
            {run.active && onCancel && (
              <Button danger block onClick={onCancel} loading={cancelling}>
                {t("RecommendationRun-Cancel")}
              </Button>
            )}
          </div>
        )}
    </div>
  );
};

export default RecommendationRunBanner;
